import { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import routesfe from '../Config/RoutesFE';
import { useLocation, useNavigate } from 'react-router-dom';
import configDetails from '../Config/Config';
import Toast from '../components copy/Toast';
import logger from '../utils/Logger';

const COMPONENT_NAME = 'UserVerification';

const UserVerification = () => {
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSentMessage, setOtpSentMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isPostLoginVerification, setIsPostLoginVerification] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Restore OTP sent state from sessionStorage on mount
  useEffect(() => {
    const otpSentFromStorage = sessionStorage.getItem('otpSent');
    const otpMessageFromStorage = sessionStorage.getItem('otpSentMessage');
    
    if (otpSentFromStorage === 'true') {
      setOtpSent(true);
      if (otpMessageFromStorage) {
        setOtpSentMessage(otpMessageFromStorage);
      }
    }
  }, []);
  
  useEffect(() => {
    const initializeVerification = async () => {
      setIsCheckingStatus(true);
      setError(''); // Clear any previous errors
      const emailFromState = location.state?.email;
      const isPostLogin = location.state?.isPostLoginVerification;
      
      setIsPostLoginVerification(!!isPostLogin);
      
      // For post-login verification of unconfirmed users, don't try to get currentAuthenticatedUser
      // because unconfirmed users are not considered authenticated by Cognito
      if (isPostLogin && emailFromState) {
        setEmail(emailFromState);
        
        // Try to get name from localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setUserName(userData.name || 'User');
          } catch (e) {
            setUserName('User');
          }
        } else {
          setUserName('User');
        }
        
        setIsCheckingStatus(false);
        return;
      }
      
      // For signup verification flow, try to get current user
      try {
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        
        // Extract email and name
        const userEmail = emailFromState || user.attributes.email;
        const userDisplayName = user.attributes.name || 'User';
        
        setEmail(userEmail);
        setUserName(userDisplayName);
        
        // Check if email is already verified
        if (user.attributes.email_verified) {
          // If already verified, redirect to login
          setTimeout(() => {
            window.location.href = routesfe.LOGIN;
          }, 500);
          return;
        }
        
        // If not verified, show the verification form
        setIsCheckingStatus(false);
      } catch (err) {
        // If we get here in non-post-login mode with an error, show it
        if (!isPostLogin) {
          logger.error('Verification initialization failed', {
            component: COMPONENT_NAME,
            action: 'INIT_FAILED',
            error: err,
            context: {
              errorCode: err.code,
              errorName: err.name,
              errorMessage: err.message,
              errorStack: err.stack,
              timestamp: Date.now()
            }
          });
          setError('Unable to load verification page. Please try again.');
          setIsCheckingStatus(false);
          return;
        }
        
        // Fallback: set default values
        if (emailFromState) {
          setEmail(emailFromState);
        }
        setUserName('User');
        setError('');
        setIsCheckingStatus(false);
      }
    };
    
    initializeVerification();
  }, [location.state, navigate]);
  const startCountdown = () => {
    setCountdown(20);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev > 0) return prev - 1;
        clearInterval(timer);
        return 0;
      });
    }, 1000);
  };
  // Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOtpSentMessage(''); // Clear previous message
    setOtp(''); // Clear OTP input field
    
    logger.info('OTP send attempt started', {
      component: COMPONENT_NAME,
      action: 'SEND_OTP_ATTEMPT',
      context: {
        email,
        isPostLoginVerification,
        timestamp: Date.now()
      }
    });
    
    try {
      if (isPostLoginVerification) {
        // For post-login verification of unconfirmed users, resend signup code
        await Auth.resendSignUp(email.trim());
      } else {
        // For signup verification, verify current user attribute
        await Auth.verifyCurrentUserAttribute('email');
      }
      
      logger.info('OTP sent successfully', {
        component: COMPONENT_NAME,
        action: 'SEND_OTP_SUCCESS',
        context: {
          email,
          isPostLoginVerification,
          timestamp: Date.now()
        }
      });
      
      setOtpSent(true);
      const message = `✅ OTP has been sent to ${email}`;
      setOtpSentMessage(message);
      
      // Store in sessionStorage to persist across refreshes
      sessionStorage.setItem('otpSent', 'true');
      sessionStorage.setItem('otpSentMessage', message);
      
      startCountdown();
    } catch (err) {
      logger.error('OTP send failed', {
        component: COMPONENT_NAME,
        action: 'SEND_OTP_FAILED',
        error: err,
        context: {
          email,
          isPostLoginVerification,
          errorCode: err.code,
          errorName: err.name,
          errorMessage: err.message,
          errorStack: err.stack,
          timestamp: Date.now()
        }
      });
      
      setError(err.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };
  // Resend OTP (no confirmation popup)
  const handleResendOtp = async (e) => {
    e.preventDefault();
    if (countdown > 0) return;
    // Clear OTP field and resend
    setOtp('');
    // Just resend without confirmation popup for better UX
    handleSendOtp(e);
  };
  // Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    logger.info('OTP verification attempt started', {
      component: COMPONENT_NAME,
      action: 'VERIFY_OTP_ATTEMPT',
      context: {
        email,
        isPostLoginVerification,
        timestamp: Date.now()
      }
    });
    
    try {
      // Trim both email and OTP to remove any whitespace
      const trimmedEmail = email.trim();
      const trimmedOtp = otp.trim();
      
      // Use different verification methods based on the flow
      if (isPostLoginVerification) {
        // For post-login verification of unconfirmed users, use confirmSignUp
        // This matches the resendSignUp call we made earlier
        await Auth.confirmSignUp(trimmedEmail, trimmedOtp);
      } else {
        // For signup verification where user is authenticated, use verifyCurrentUserAttributeSubmit
        // This matches the verifyCurrentUserAttribute call we made earlier
        await Auth.verifyCurrentUserAttributeSubmit('email', trimmedOtp);
      }
      
      logger.info('OTP verification successful', {
        component: COMPONENT_NAME,
        action: 'VERIFY_OTP_SUCCESS',
        context: {
          email,
          isPostLoginVerification,
          timestamp: Date.now()
        }
      });
      
      setToast({ message: 'Email verified successfully! 🎉', type: 'success' });
      setIsRedirecting(true);
      
      // Clear sessionStorage since verification is complete
      sessionStorage.removeItem('otpSent');
      sessionStorage.removeItem('otpSentMessage');
      
      // Store user info to localStorage for persistence across redirects
      try {
        const verifiedUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
        if (verifiedUser && verifiedUser.attributes) {
          const userInfo = {
            name: verifiedUser.attributes.name || 'User',
            email: verifiedUser.attributes.email || email,
            userSub: verifiedUser.attributes.sub,
          };
          localStorage.setItem('user', JSON.stringify(userInfo));
          
          logger.info('User data stored after email verification', {
            component: COMPONENT_NAME,
            action: 'USER_DATA_STORED',
            context: { userSub: userInfo.userSub, email: userInfo.email }
          });
        }
      } catch (err) {
        // If we can't get user, that's okay - they'll login again
        logger.warn('Could not store user data after verification', {
          component: COMPONENT_NAME,
          action: 'STORE_USER_DATA_FAILED',
          error: err.message
        });
      }
      
      // For signup flow, redirect to login
      if (!isPostLoginVerification) {
        setTimeout(() => {
          window.location.href = routesfe.LOGIN;
        }, 1500);
        return;
      }
      
      // For post-login verification, redirect back to login page with success message
      // User can now login again with their credentials and will proceed normally
      setTimeout(() => {
        navigate(routesfe.LOGIN, { 
          state: { 
            verificationSuccess: true,
            verifiedEmail: email
          },
          replace: true 
        });
      }, 1500);
    } catch (err) {
      logger.error('OTP verification failed', {
        component: COMPONENT_NAME,
        action: 'VERIFY_OTP_FAILED',
        error: err,
        context: {
          email,
          isPostLoginVerification,
          errorCode: err.code,
          errorName: err.name,
          errorMessage: err.message,
          errorStack: err.stack,
          timestamp: Date.now()
        }
      });
      
      setError(err.message || 'Invalid OTP. Please try again.');
      setIsRedirecting(false);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle logout for post-login verification
  const handleLogout = async () => {
    setIsLogoutLoading(true);
    
    logger.info('User logout attempt from verification page', {
      component: COMPONENT_NAME,
      action: 'LOGOUT_ATTEMPT',
      context: {
        email,
        isPostLoginVerification,
        timestamp: Date.now()
      }
    });
    
    try {
      await Auth.signOut({ global: true });
      localStorage.removeItem('user');
      
      // Clear verification state
      sessionStorage.removeItem('otpSent');
      sessionStorage.removeItem('otpSentMessage');
      
      logger.info('User logout successful', {
        component: COMPONENT_NAME,
        action: 'LOGOUT_SUCCESS',
        context: {
          email,
          timestamp: Date.now()
        }
      });
      
      navigate(routesfe.LOGIN, { replace: true });
    } catch (err) {
      logger.error('User logout failed', {
        component: COMPONENT_NAME,
        action: 'LOGOUT_FAILED',
        error: err,
        context: {
          email,
          errorCode: err.code,
          errorName: err.name,
          errorMessage: err.message,
          errorStack: err.stack,
          timestamp: Date.now()
        }
      });
      
      setError('Error logging out. Please try again.');
    } finally {
      setIsLogoutLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      
      {/* Redirecting overlay */}
      {isRedirecting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4 max-w-sm mx-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-sky-600"></div>
            <h3 className="text-xl font-bold text-slate-800">Redirecting...</h3>
            <p className="text-slate-600 text-center">Taking you to the login page</p>
          </div>
        </div>
      )}
      
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-100 via-slate-100 to-emerald-50">
      {/* Loading state while checking verification status */}
      {isCheckingStatus ? (
        <div className="bg-white shadow-2xl rounded-2xl p-10 w-full max-w-md border border-slate-200 relative">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
            <p className="text-slate-600 text-center">Checking verification status...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-2xl rounded-2xl p-10 w-full max-w-md border border-slate-200 relative">
          {/* Decorative top bar */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-24 h-2 bg-sky-400 rounded-full shadow-md" />
          {/* Greeting with name */}
          <h2 className="text-3xl font-extrabold text-center text-sky-600 mb-2 tracking-tight drop-shadow-sm">
            Hey {userName || 'there'}! 👋
          </h2>
          <h3 className="text-xl font-bold text-center text-slate-700 mb-2">
            One Last Step! <span role="img" aria-label="rocket">🚀</span>
          </h3>
        <p className="text-slate-600 text-center mb-7 text-base">
          {isPostLoginVerification 
            ? 'Please verify your email to access your account.' 
            : 'Please verify your email to continue.'}
        </p>
        {/* OTP Sent Success Message */}
        {otpSentMessage && (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-700 px-4 py-3 rounded-lg mb-4 text-center animate-fade-in flex items-center justify-center gap-2">
            <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium">{otpSentMessage}</span>
          </div>
        )}
        {/* Error Message */}
        {error && (
          <div className="bg-rose-50 border border-rose-300 text-rose-700 px-4 py-2 rounded-lg mb-4 text-center animate-fade-in">
            {error}
          </div>
        )}
        {/* Email Address Display */}
        <div className="mb-7">
          <label htmlFor="email" className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            disabled
            className="w-full px-4 py-2 border border-slate-200 rounded-md bg-slate-100 text-slate-700 font-medium shadow-inner focus:outline-none focus:ring-2 focus:ring-sky-200"
          />
        </div>
        {/* Send OTP or OTP Input/Verify/Resend */}
        {!otpSent ? (
          <>
            <button
              onClick={handleSendOtp}
              disabled={loading || countdown > 0}
              className={`w-full py-2 text-white font-bold rounded-lg shadow transition duration-150 ${
                loading || countdown > 0
                  ? 'bg-slate-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-500'
              }`}
            >
              {loading
                ? 'Sending OTP...'
                : countdown > 0
                ? `Resend OTP in ${countdown}s`
                : 'Send OTP'}
            </button>
            
            {/* Logout button for post-login verification - before OTP is sent */}
            {isPostLoginVerification && (
              <button
                onClick={handleLogout}
                disabled={isLogoutLoading || loading}
                className="w-full mt-4 py-2 text-red-600 font-bold rounded-lg border border-red-400 hover:bg-red-50 transition duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLogoutLoading ? 'Logging out...' : 'Logout'}
              </button>
            )}
          </>
        ) : (
          <>
            {/* OTP Input */}
            <div className="mb-7">
              <label htmlFor="otp" className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                Enter OTP
              </label>
              <input
                id="otp"
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-md shadow-inner focus:outline-none focus:ring-2 focus:ring-sky-400 text-slate-700 font-medium"
                required
                autoFocus
                maxLength={6}
                inputMode="numeric"
              />
            </div>
            {/* Verify OTP */}
            <button
              onClick={handleVerifyOtp}
              disabled={loading}
              className={`w-full py-2 text-white font-bold rounded-lg shadow transition duration-150 ${
                loading ? 'bg-slate-300 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-sky-600 hover:to-emerald-500'
              }`}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            {/* Resend OTP */}
            <button
              onClick={handleResendOtp}
              disabled={loading || countdown > 0}
              className={`w-full mt-4 py-2 text-sky-600 font-bold rounded-lg border border-sky-400 hover:bg-sky-50 transition duration-150 ${
                loading || countdown > 0 ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              {countdown > 0 ? `Resend OTP in ${countdown}s` : 'Resend OTP'}
            </button>
            
            {/* Logout button for post-login verification */}
            {isPostLoginVerification && (
              <button
                onClick={handleLogout}
                disabled={isLogoutLoading || loading}
                className="w-full mt-4 py-2 text-red-600 font-bold rounded-lg border border-red-400 hover:bg-red-50 transition duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLogoutLoading ? 'Logging out...' : 'Logout'}
              </button>
            )}
          </>
        )}
        
        {/* Back to Home or Logout - show for signup verification */}
          {!isPostLoginVerification && (
            <button
              onClick={async () => {
                // Logout the user to prevent redirect loop
                try {
                  await Auth.signOut();
                  localStorage.removeItem('user');
                  sessionStorage.removeItem('otpSent');
                  sessionStorage.removeItem('otpSentMessage');
                } catch (err) {
                  logger.error('Logout failed during back to home', {
                    component: COMPONENT_NAME,
                    action: 'BACK_TO_HOME_LOGOUT_FAILED',
                    error: err,
                    context: {
                      email,
                      errorCode: err.code,
                      errorName: err.name,
                      errorMessage: err.message,
                      errorStack: err.stack,
                      timestamp: Date.now()
                    }
                  });
                }
                window.location.href = routesfe.LANDING;
              }}
              className="w-full mt-8 text-sky-500 hover:text-sky-700 text-xs text-center font-semibold tracking-wide underline underline-offset-2 transition"
            >
              ← Back to Home
            </button>
          )}
        </div>
      )}
      </div>
    </>
  );
};
export default UserVerification;